import {Result} from "neverthrow"
import {getResource, getFile, OkDocument, ErrorDocument} from "./common"
import {Cents} from "./transaction"
import {Counterparty} from "./counterparty"

export type ManagedSolutionsCustomer = {
	type: "customer"
	id: string
	attributes: {
		userIds: string[]
		customerType?: string
		status?: string
		createdAt?: string
	}
}

export type ManagedSolutionsAccount = {
	type: "account"
	id: string
	attributes: {
		routingNumber: string
		accountNumber: string
		status: string
		/** Present only when the account is Closed. */
		closeReason?: string
		/** Omitted for some viewers (e.g. operational admin) on non-operational accounts. */
		balance?: number
		userIds: string[]
		createdAt?: string
		accountType?: string
		tags: Record<string, string>
	}
}

export type ManagedSolutionsApplication = {
	type: "application"
	id: string
	attributes: {
		status: string
		createdAt: string
		userIds: string[]
	}
	relationships?: {
		applicationForm?: {data: {id: string} | null}
		customer?: {data: {id: string} | null}
	}
}

export type ManagedSolutionsApplicationForm = {
	type: "applicationForm"
	id: string
	attributes: {
		createdAt: string
		updatedAt: string
		tags: Record<string, string>
		stage?: string
		userIds: string[]
	}
	relationships?: {
		application?: {data: {id: string} | null}
	}
}

export type ManagedSolutionsTransaction = {
	type: "transaction"
	id: string
	attributes: {
		amount: number
		direction: string
		transactionType: string
		createdAt: string
		balance: number
		summary: string
		tags: Record<string, any>
		userIds: string[]
		counterparty?: {
			name: string
			routingNumber: string
			accountNumber: string
			accountType: string
		}
		addenda?: string
		companyName?: string
		atmName?: string
		atmLocation?: {
			lat: number
			lon: number
		}
		merchant?: {
			name: string
			type: string
			category: string
			location?: {
				lat: number
				lon: number
			}
			id?: string
		}
	}
	relationships: {
		account: {
			data: {
				id: string
			}
		}
		customer?: {
			data: {
				id: string
			}
		}
		customers?: {
			data: Array<{
				id: string
			}>
		}
	}
}

export type ManagedSolutionsAccountsResponse = {
	data: ManagedSolutionsAccount[]
	meta?: {
		pagination: {
			total: number
			limit: number
			offset: number
		}
	}
}

export type FindManagedSolutionsTransactionsParams = {
	accessToken: string
	offset: number
	limit: number
	customerId?: string
	accountId?: string
	searchQuery?: string
	sort?: string
	since?: string
	until?: string
	types?: string[]
	fromAmount?: Cents | ""
	toAmount?: Cents | ""
}

export async function findManagedSolutionsCustomers(
	accessToken: string,
	offset: number,
	limit: number,
	sort?: string
): Promise<Result<OkDocument<ManagedSolutionsCustomer[]>, ErrorDocument>> {
	const query = {
		page: {
			limit,
			offset,
		},
		...(sort ? {sort} : {}),
	}

	return await getResource<ManagedSolutionsCustomer[]>("ready-to-launch/customers", accessToken, query)
}

export async function findManagedSolutionsAccounts(
	accessToken: string,
	offset: number,
	limit: number,
	customerId?: string,
	tags?: Record<string, string>,
	sort?: string
): Promise<Result<OkDocument<ManagedSolutionsAccount[]>, ErrorDocument>> {
	const query = {
		page: {
			limit,
			offset,
		},
		...(customerId || tags
			? {
					filter: {
						...(customerId && {customerId: parseInt(customerId, 10)}),
						...(tags && {tags: JSON.stringify(tags)}),
					},
			  }
			: {}),
		...(sort ? {sort} : {}),
	}

	return await getResource<ManagedSolutionsAccount[]>("ready-to-launch/accounts", accessToken, query)
}

export async function findManagedSolutionsApplications(
	accessToken: string,
	offset: number,
	limit: number,
	sort?: string
): Promise<Result<OkDocument<ManagedSolutionsApplication[]>, ErrorDocument>> {
	const query = {
		page: {
			limit,
			offset,
		},
		...(sort ? {sort} : {}),
	}

	return await getResource<ManagedSolutionsApplication[]>("ready-to-launch/applications", accessToken, query)
}

export async function findManagedSolutionsApplicationForms(
	accessToken: string,
	offset: number,
	limit: number,
	sort?: string
): Promise<Result<OkDocument<ManagedSolutionsApplicationForm[]>, ErrorDocument>> {
	const query = {
		page: {
			limit,
			offset,
		},
		...(sort ? {sort} : {}),
	}

	return await getResource<ManagedSolutionsApplicationForm[]>("ready-to-launch/application-forms", accessToken, query)
}

export async function getManagedSolutionsApplication(
	accessToken: string,
	applicationId: string
): Promise<Result<OkDocument<ManagedSolutionsApplication>, ErrorDocument>> {
	const result = await getResource<ManagedSolutionsApplication>(
		`ready-to-launch/applications/${applicationId}`,
		accessToken
	)
	return result.map((doc) => ({
		...doc,
		data: doc.data,
	}))
}

export async function getManagedSolutionsApplicationForm(
	accessToken: string,
	applicationFormId: string
): Promise<Result<OkDocument<ManagedSolutionsApplicationForm>, ErrorDocument>> {
	const result = await getResource<ManagedSolutionsApplicationForm>(
		`ready-to-launch/application-forms/${applicationFormId}`,
		accessToken
	)
	return result.map((doc) => ({
		...doc,
		data: doc.data,
	}))
}

export async function getManagedSolutionsCustomer(
	accessToken: string,
	customerId: string
): Promise<Result<OkDocument<ManagedSolutionsCustomer>, ErrorDocument>> {
	const query = {
		page: {
			limit: 1,
			offset: 0,
		},
		filter: {
			customerId: parseInt(customerId, 10),
		},
	}

	const result = await getResource<ManagedSolutionsCustomer[]>("ready-to-launch/customers", accessToken, query)
	return result.map((doc) => {
		if (doc.data.length === 0) {
			throw new Error("Customer not found")
		}
		return {
			...doc,
			data: doc.data[0],
		}
	})
}

export async function findManagedSolutionsTransactions({
	accessToken,
	offset,
	limit,
	customerId,
	accountId,
	searchQuery,
	sort,
	since,
	until,
	types,
	fromAmount,
	toAmount,
}: FindManagedSolutionsTransactionsParams): Promise<Result<OkDocument<ManagedSolutionsTransaction[]>, ErrorDocument>> {
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			...(customerId && {customerId: parseInt(customerId, 10)}),
			...(accountId && {accountId: parseInt(accountId, 10)}),
			...(searchQuery && {query: searchQuery}),
			...(since && {since}),
			...(until && {until}),
			...(types && types.length > 0 && {type: types}),
			...(fromAmount !== "" && fromAmount !== undefined && {fromAmount}),
			...(toAmount !== "" && toAmount !== undefined && {toAmount}),
		},
		...(sort ? {sort} : {}),
	}

	return await getResource<ManagedSolutionsTransaction[]>("ready-to-launch/transactions", accessToken, query)
}

export async function exportManagedSolutionsTransactions(
	accessToken: string,
	customerId?: string,
	accountId?: string,
	since?: string,
	until?: string,
	types?: string[],
	fromAmount?: Cents | "",
	toAmount?: Cents | ""
) {
	const query = {
		filter: {
			...(customerId && {customerId: parseInt(customerId, 10)}),
			...(accountId && {accountId: parseInt(accountId, 10)}),
			...(since && {since}),
			...(until && {until}),
			...(types && types.length > 0 && {type: types}),
			...(fromAmount !== "" && fromAmount !== undefined && {fromAmount}),
			...(toAmount !== "" && toAmount !== undefined && {toAmount}),
		},
	}

	return await getFile("ready-to-launch/transactions", accessToken, query)
}

export async function exportManagedSolutionsAccounts(
	accessToken: string,
	customerId?: string,
	tags?: Record<string, string>,
	sort?: string
) {
	const query = {
		...(customerId || tags
			? {
					filter: {
						...(customerId && {customerId: parseInt(customerId, 10)}),
						...(tags && {tags: JSON.stringify(tags)}),
					},
			  }
			: {}),
		...(sort ? {sort} : {}),
	}

	return await getFile("ready-to-launch/accounts", accessToken, query)
}

export async function exportManagedSolutionsCustomers(accessToken: string, sort?: string) {
	const query = {
		...(sort ? {sort} : {}),
	}

	return await getFile("ready-to-launch/customers", accessToken, query)
}

export async function exportManagedSolutionsApplications(accessToken: string, sort?: string) {
	const query = {
		...(sort ? {sort} : {}),
	}

	return await getFile("ready-to-launch/applications", accessToken, query)
}

export async function exportManagedSolutionsApplicationForms(accessToken: string, sort?: string) {
	const query = {
		...(sort ? {sort} : {}),
	}

	return await getFile("ready-to-launch/application-forms", accessToken, query)
}

export async function exportManagedSolutionsUsers(accessToken: string, userRole?: string, sort?: string) {
	const query = {
		...(userRole ? {filter: {userRole}} : {}),
		...(sort ? {sort} : {}),
	}

	return await getFile("ready-to-launch/users", accessToken, query)
}

export async function getManagedSolutionsAccount(
	accessToken: string,
	accountId: string
): Promise<Result<OkDocument<ManagedSolutionsAccount>, ErrorDocument>> {
	const result = await getResource<ManagedSolutionsAccount>(`ready-to-launch/accounts/${accountId}`, accessToken)
	return result.map((doc) => ({
		...doc,
		data: doc.data,
	}))
}

export type ManagedSolutionsUser = {
	type: "whiteLabelAppUser"
	id: string
	attributes: {
		userId: string
		userRole: string
		status: string
		createdAt: string
		applicationFormStage?: string
		applicationStatus?: string
	}
	relationships?: {
		customer?: {data: {id: string} | null}
		applicationForm?: {data: {id: string} | null}
		application?: {data: {id: string} | null}
	}
}

export async function findManagedSolutionsUsers(
	accessToken: string,
	offset: number,
	limit: number,
	userRole?: string,
	sort?: string
): Promise<Result<OkDocument<ManagedSolutionsUser[]>, ErrorDocument>> {
	const query = {
		page: {
			limit,
			offset,
		},
		...(userRole ? {filter: {userRole}} : {}),
		...(sort ? {sort} : {}),
	}

	return await getResource<ManagedSolutionsUser[]>("ready-to-launch/users", accessToken, query)
}

export async function findManagedSolutionsCustomerByUserId(
	accessToken: string,
	userId: string
): Promise<Result<OkDocument<ManagedSolutionsCustomer[]>, ErrorDocument>> {
	const query = {
		page: {limit: 1, offset: 0},
		filter: {userId},
	}
	return await getResource<ManagedSolutionsCustomer[]>("ready-to-launch/customers", accessToken, query)
}

export async function getManagedSolutionsUser(
	accessToken: string,
	userId: string
): Promise<Result<OkDocument<ManagedSolutionsUser>, ErrorDocument>> {
	const result = await getResource<ManagedSolutionsUser>(`ready-to-launch/users/${userId}`, accessToken)
	return result.map((doc) => ({
		...doc,
		data: doc.data,
	}))
}

export async function findManagedSolutionsCounterparties(
	accessToken: string,
	customerId?: string,
	userId?: string
): Promise<Result<OkDocument<Counterparty[]>, ErrorDocument>> {
	const query = {
		page: {limit: 100, offset: 0},
		filter: {
			...(customerId ? {customerId} : {}),
			...(userId ? {userId} : {}),
		},
	}
	return await getResource<Counterparty[]>("ready-to-launch/counterparties", accessToken, query)
}
